import { z } from 'zod';

const ContractKpiSchema = z.object({
  id: z.number().optional(),
  kpiName: z.string().min(1, 'KPI name is required'),
  provider: z.string().optional().nullable(),
  goalValue: z.union([z.string(), z.number()]).refine(
    (val) => {
      const num = typeof val === 'string' ? parseFloat(val) : val;
      return !Number.isNaN(num) && num >= 0;
    },
    { message: 'Goal must be a number >= 0' }
  ),
  goalFormat: z.enum(['number', 'percent', 'dollars']),
  isCustomKpi: z.boolean().optional(),
  isCustomProvider: z.boolean().optional(),
  position: z.number().optional(),
  _destroy: z.boolean().optional(),
});

export type ContractKpi = z.infer<typeof ContractKpiSchema>;

const ContractSchema = z.object({
  contractId: z.number().nullable(),
  contractName: z
    .string()
    .min(1, { message: 'Contract name is required' })
    .refine((val) => !/#/.test(val), {
      message: "Contract name cannot contain # characters",
    }),
  ioFile: z.string().min(1, { message: 'IO file is required' }),
  assetId: z.string().nullable(),
  insertionOrderNumber: z.string().nullable(),
  purchaseOrderNumber: z.string().nullable(),
  startDate: z.date({ invalid_type_error: 'Start date is required' }),
  endDate: z.date({ invalid_type_error: 'End date is required' }),
  bidType: z.string().nullable(),
  bidSourceType: z.string().min(1, { message: 'Bid Source is required' }),
  pmpAllowedExchanges: z.string().nullable(),
  pricingRate: z.string().or(z.number()),
  contractCpm: z.string().or(z.number()),
  contractCpc: z.string().or(z.number()),
  multiRate: z.boolean(),
  costPlus: z.boolean(),
  advisoryBudget: z.string().or(z.number()),
  bonus: z.string().or(z.number()),
  dollarBudget: z.string().or(z.number()),
  dynamicSpendPacing: z.boolean(),
  domain: z.string().min(1, { message: 'Domain is required' }),
  salesforceId: z.string().nullable(),
  pacingStrategies: z.array(z.string()),
  slowStart: z.boolean(),
  fastCatchup: z.boolean(),
  vertical: z.string().nullable(),
  billingOption: z
    .string({ invalid_type_error: 'Billing option is required' })
    .min(1, { message: 'Billing option is required' }),
  invoicingNotes: z.string().nullable(),
  billingEmails: z
    .string({ invalid_type_error: 'Billing email address is required' })
    .min(1, { message: 'Billing email address is required' }),
  billingTerms: z
    .string({ invalid_type_error: 'Billing term is required' })
    .min(1, { message: 'Billing term is required' }),
  mapsEnabled: z.boolean(),
  attributionEnabled: z.boolean(),
  attributionFile: z.string().nullable(),
  placed: z.boolean(),
  lciMeasurement: z.boolean(),
  contractLciCampaigns: z
    .array(
      z.object({
        id: z.number().optional(),
        lci_campaign_id: z.number().int().positive(),
        campaign_type: z.literal('internal'),
        active: z.boolean().optional(),
        _destroy: z.enum(['1']).optional(),
      })
    )
    .default([]),
  contractKpis: z
    .array(ContractKpiSchema)
    .default([])
    .refine(
      (kpis) => {
        // Filter out KPIs marked for destruction
        // eslint-disable-next-line no-underscore-dangle
        const activeKpis = kpis.filter((kpi) => !kpi._destroy && kpi.kpiName);
        return activeKpis.length > 0;
      },
      { message: 'At least one KPI is required' }
    ),
  categorization: z.array(z.string()),
  isComplete: z.boolean(),
  multiOptimization: z.boolean(),
  cpaPixelUrl: z.string().nullable(),
  marginTarget: z.string().nullable(),
  storeList: z.array(z.any()),
  cpaAction: z.array(z.string()),
  kpiGoalFieldArray: z.array(
    z.object({
      key: z.string(),
      value: z.string(),
    })
  ),
  kpiGoal: z.string().nullable(),
  customBillingOptions: z.array(
    z.object({
      _destroy: z.enum(['1']).optional(),
      id: z.number().optional(),
      billingOption: z.string().optional(),
      externalId: z.string().optional(),
      reportingName: z.string().optional(),
      impressionBudget: z.string().or(z.number()).nullable(),
      rate: z.string().or(z.number()).nullable(),
      dollarBudget: z.string().or(z.number()).nullable(),
      startDate: z.date().nullable().optional().default(null),
      endDate: z.date().nullable().optional().default(null),
      notes: z.string().optional(),
    })
  ),
  selectedClientName: z.string(),
  selectedClientId: z.number({ required_error: 'Client is required' }),
  selectedAdvertiserId: z.number({ required_error: 'Advertiser is required' }).nullable(),
  selectedAdvertiserName: z.string(),
  billingModel: z.string().nullable(),
  frequencyCapEnabled: z.boolean(),
  frequencyCapping: z.string(),
  maxUserViewsPerPeriod: z.string().or(z.number()),
  performanceCapEnabled: z.boolean(),
  clickFrequencyCapping: z.string(),
  maxUserClicksPerPeriod: z.string().or(z.number()),
  locked_for_advertiser_change: z.boolean().optional().default(false),
  clientAdvEditable: z.boolean().optional().default(false),
});

export type Contract = z.infer<typeof ContractSchema>;

export default ContractSchema;
